import { Army } from "@/domain/army";
import { ConfirmDialog } from "@/ui/confirm-dialog";
import { useDatabase } from "@/ui/database-provider";
import { useGameContentLookup } from "@/ui/game-content-provider";
import { useDialog } from "@/ui/hooks/use-dialog";
import { useNavigate } from "@/ui/hooks/use-navigate";
import {
  MoreMenu,
  MoreMenuDialogDisclosureItem,
  MoreMenuItem,
  MoreMenuSeparator,
} from "@/ui/more-menu";
import { UpgradeArmyRulesetDialog } from "@/ui/upgrade-army-ruleset-dialog";
import { interactiveText, primaryTheme } from "@/ui/utils.css";
import { DialogDisclosure } from "@ariakit/react";
import { EditArmyDialog } from "../army-form-dialog";
import { useArmyOp } from "../army-provider/context";
import { DialogTrigger } from "../dialog-trigger";
import { MainImageFormDialog, PosterImageFormDialog } from "../main-image-picker/MainImageDialog";

function UpdateArmyRulesetMenuItem(props: { army: Army }) {
  const { isOpen, setOpen, store } = useDialog();

  return (
    <>
      {isOpen && <UpgradeArmyRulesetDialog store={store} army={props.army} />}
      <MoreMenuItem onClick={setOpen}>Upgrade to latest</MoreMenuItem>
    </>
  );
}

function EditArmyMenuItem(props: { army: Army }) {
  return (
    <DialogTrigger>
      <EditArmyDialog army={props.army} />
      <MoreMenuDialogDisclosureItem>Edit</MoreMenuDialogDisclosureItem>
    </DialogTrigger>
  );
}

function ChangeImageMenuItem(props: { army: Army }) {
  const hasImage = !!props.army.mainImage;

  return (
    <DialogTrigger>
      <MainImageFormDialog army={props.army} />
      <MoreMenuDialogDisclosureItem>
        {hasImage ? "Change image" : "Add image"}
      </MoreMenuDialogDisclosureItem>
    </DialogTrigger>
  );
}

export function ChangePosterImageButton(props: { army: Army }) {
  const { army } = props;

  if (army.isShared) {
    return null;
  }

  return (
    <DialogTrigger>
      <PosterImageFormDialog army={army} />
      <DialogDisclosure className={interactiveText}>
        {army.posterImage ? "Change poster image" : "Add poster image"}
      </DialogDisclosure>
    </DialogTrigger>
  );
}

function DuplicateArmyMenuItem(props: { army: Army }) {
  const database = useDatabase();
  const navigate = useNavigate();
  const { isOpen, setOpen, store } = useDialog();

  return (
    <>
      {isOpen && (
        <ConfirmDialog
          store={store}
          heading={`Duplicate ${props.army.shortTypeLabel}?`}
          message={`${props.army.name} with all its units will be duplicated.`}
          submit="Duplicate"
          submitTheme={primaryTheme}
          onSubmit={async () => {
            const armyId = await database.duplicateArmy(props.army.data);
            navigate(`~/army/a/${armyId}`);
          }}
        />
      )}

      <MoreMenuItem onClick={setOpen}>Duplicate</MoreMenuItem>
    </>
  );
}

function DeleteArmyMenuItem(props: { army: Army }) {
  const database = useDatabase();
  const navigate = useNavigate();
  const { isOpen, setOpen, store } = useDialog();

  return (
    <>
      {isOpen && (
        <ConfirmDialog
          heading={`Delete ${props.army.shortTypeLabel}?`}
          message={`Are you sure you want to delete "${props.army.name}"?`}
          store={store}
          onSubmit={async () => {
            await database.deleteArmy(props.army.data);
            navigate(`~/army`);
          }}
          submit="Delete"
        />
      )}

      <MoreMenuItem onClick={setOpen}>Delete</MoreMenuItem>
    </>
  );
}

export function ArmyActionsMenu(props: { iconColor?: string }) {
  const armyOp = useArmyOp();
  const { latestRuleset } = useGameContentLookup();

  if (!armyOp.isSuccess) {
    return null;
  }

  const army = armyOp.value;
  const isOutdated = army.ruleset.version < latestRuleset.version;

  return (
    <MoreMenu fallbackLabel="Army actions" iconColor={props.iconColor}>
      {isOutdated && <UpdateArmyRulesetMenuItem army={army} />}
      <EditArmyMenuItem army={army} />

      <ChangeImageMenuItem army={army} />

      <DuplicateArmyMenuItem army={army} />

      <MoreMenuSeparator />

      <DeleteArmyMenuItem army={army} />
    </MoreMenu>
  );
}
